'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { VideoItem } from '../type';
import { useLocale } from 'use-intl';
import { getCMSIntlVal } from '../utils/common';
import ButtonPaging from '../component/ButtonPaging';
import Image from '../../../image';
import { useMedia } from '@ssv-website/hooks';
import { Tooltip } from 'tdesign-react';

export interface VideoCardItemProps {
  item: VideoItem;
  /** 点击整体卡片 */
  onClick?: () => void;
  /** 外层自定义类名（用于布局） */
  className?: string;
}

/**
 * 2025 视频卡片
 * 对应 Figma 「视频播放」PC + 移动端样式
 */
const VideoCardItem: React.FC<VideoCardItemProps> = ({
  item,
  onClick,
  className = '',
}) => {
  const locale = useLocale();
  const showTitle = locale === 'en';
  const { poster } = item;
  const title = getCMSIntlVal(item, 'title', locale);
  return (
    <div
      className={`group relative bg-white cursor-pointer select-none w-[334px] mobile:w-[272px] h-[272px] mobile:h-[216px] ${className}`}
      onClick={onClick}
    >
      {/* 封面图 */}
      <div className="relative w-full h-[188px] overflow-hidden mobile:h-[152px]">
        <Image
          src={poster}
          alt={title}
          loading="lazy"
          width={334}
          height={188}
          className="!w-full !h-full group-hover:scale-110 transition-all duration-300"
          style={{
            objectFit: 'cover',
            backgroundColor: '#ffffff',
          }}
        />

        {/* 播放按钮蒙层 */}
        <div
          className="absolute z-[1] size-[42px] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{
            background: `url(https://cdn-digital.ssv.qq.com/upload//s2025/play-surface.png) no-repeat center center / 100% 100%`,
          }}
        ></div>
      </div>

      {/* 文本信息区域 */}
      <div className="px-4 py-[18px] w-full bg-white mobile:px-3 mobile:py-[14px] min-h-[84px] mobile:min-h-[64px] overflow-hidden">
        <div className="flex items-start gap-x-3 relative mobile:gap-x-2">
          <Tooltip content={title} delay={150} disabled={!showTitle}>
            <p className="flex-1 min-w-0 text-[#121C59] text-[16px] leading-[1.5] font-400 mobile:text-[12px] line-clamp-2 break-all">
              {title}
            </p>
          </Tooltip>

          {/* 右侧小箭头 */}
          <div
            className="absolute right-0 top-0 invisible group-hover:visible group-hover:static translate-x-[40px] group-hover:translate-x-0 size-6 mobile:size-[18px] transition-all duration-300 shrink-0 mobile:translate-x-[0] mobile:visible mobile:static"
            style={{
              background: `url(https://cdn-digital.ssv.qq.com/upload//s2025/more-icon-251218.png) no-repeat left top / 100% 100%`,
            }}
          ></div>
        </div>
      </div>
    </div>
  );
};

const VideoCardGrid: React.FC<{ list: VideoItem[] }> = ({ list }) => {
  const [page, setPage] = useState(1);
  const [isFading, setIsFading] = useState(false);
  const isMobile = useMedia('mobile');
  const pageSize = isMobile ? list.length : 8;

  useEffect(() => {
    if (isMobile) {
      setPage(1);
    }
  }, [isMobile]);

  const total = useMemo(() => {
    return Math.ceil(list.length / pageSize);
  }, [list.length, pageSize]);

  const renderList = useMemo(() => {
    return list.slice((page - 1) * pageSize, page * pageSize);
  }, [list, page, pageSize]);

  const handlePageChange = (newPage: number) => {
    setIsFading(true);
    setTimeout(() => {
      setPage(newPage);
      setTimeout(() => {
        setIsFading(false);
      }, 50);
    }, 300);
  };

  const handleCardClick = (item: VideoItem) => {
    window.open(item.url, '_blank');
  };

  return (
    <>
      <div
        className={`min-h-[556px] mobile:min-h-0 mt-[32px] mobile:mt-[24px] w-fit mobile:mr-5 grid grid-cols-4 gap-3 mobile:gap-1 mobile:grid-cols-none mobile:grid-rows-2 mobile:grid-flow-col mobile:auto-cols-max transition-opacity duration-300 ${
          isFading ? 'opacity-0' : 'opacity-100'
        }`}
      >
        {renderList.map((item) => (
          <VideoCardItem
            key={item.id}
            item={item}
            onClick={() => handleCardClick(item)}
          />
        ))}
      </div>
      <div className="mobile:hidden">
        <ButtonPaging
          className="!mt-[32px] mobile:!mt-[24px]"
          page={page}
          next={() => handlePageChange(page + 1)}
          prev={() => handlePageChange(page - 1)}
          total={total}
        />
      </div>
    </>
  );
};

export default VideoCardGrid;
